/**
 * SharedLiving feature gate.
 *
 * The backend folds the group feature flags into the `access_control` array
 * returned by `/users/profile` (it does NOT return the raw `is_*_active`
 * booleans). The mapping (backend routes/users.js `/profile`) is:
 *   1 = peltask, 2 = mym, 3 = mobile_app, 4 = dayprogram, 5 = sharedliving.
 *
 * So we read `access_control` — the single reliable source — and also accept a
 * direct `is_sharedliving_active` flag if a future build ever sets one.
 */
const TRUTHY = new Set([1, '1', true, 'true']);

const ACCESS_CONTROL_MYM = 2;
const ACCESS_CONTROL_SHAREDLIVING = 5;

const getAccessControlValues = () => {
  const raw = localStorage.getItem('accessControl');
  if (!raw) return [];
  return raw
    .split(',')
    .map(v => Number(v))
    .filter(v => !Number.isNaN(v));
};

export const isSharedLivingActive = () => {
  if (getAccessControlValues().includes(ACCESS_CONTROL_SHAREDLIVING)) {
    return true;
  }
  const raw = localStorage.getItem('is_sharedliving_active');
  return TRUTHY.has(raw) || TRUTHY.has(Number(raw));
};

/**
 * `is_mym_active` gates ALL edit affordances for a group (prescriptions,
 * patient profile, allergies/ICD/insurance/medical records) and the
 * Treatment / CurrentMeds / LOA reports. Applies to every role, not just
 * caregivers (backend SL-073 / SL-073b / SL-042b). Sourced from
 * `access_control` (value 2). Defaults to active when access_control is empty
 * so existing groups are unaffected.
 */
export const isMymActive = () => {
  const values = getAccessControlValues();
  if (values.length === 0) {
    return true;
  }
  return values.includes(ACCESS_CONTROL_MYM);
};

/**
 * Role 7 = SharedLiving caregiver.
 */
export const isCaregiverRole = () => Number(localStorage.getItem('roleId')) === 7;

/**
 * Live source of truth for the caregiver's assigned patients. Persisted at
 * login from `/users/profile` (NOT decoded from the JWT) so assignment changes
 * are reflected without a re-login.
 */
export const getAtHomePatients = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem('athomePatients'));
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

export default isSharedLivingActive;
